import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import DashboardStats from './components/DashboardStats';
import { supabase } from '../../lib/supabase';

import SuppliersTab from './components/SuppliersTab';


import OrdersTab from './components/OrdersTab';
import TranslationsTab from './components/TranslationsTab';
import ReportsTab from './components/ReportsTab';


import SponsorBannersTab from './components/SponsorBannersTab';
import OrderLifecycleTab from './components/OrderLifecycleTab';
import SupplierApplicationsTab from './components/SupplierApplicationsTab';
import CoffeeManagementTab from './components/CoffeeManagementTab';

const AdminDashboard = () => {
  const [language, setLanguage] = useState('en');
  const [activeTab, setActiveTab] = useState('reports');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const savedLanguage = localStorage.getItem('language') || 'en';
      setLanguage(savedLanguage);

      // TEMPORARY: Check for mock admin authentication
      const isAdminAuthenticated = localStorage.getItem('isAdminAuthenticated') === 'true';
      
      if (!isAdminAuthenticated) {
        // Only check real Supabase session if mock auth is not set
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          localStorage.removeItem('isAdminAuthenticated');
          localStorage.removeItem('rememberAdmin');
          navigate('/admin-login');
        }
      }
    };

    checkAuth();
  }, [navigate]);

  const tabs = [
    {
      id: 'reports',
      label: { en: 'Reports', zh: '报告' },
      icon: 'BarChart3'
    },
    {
      id: 'order-lifecycle',
      label: { en: 'Order Lifecycle', zh: '订单生命周期' },
      icon: 'PackageCheck'
    },
    {
      id: 'orders',
      label: { en: 'Queries', zh: '查询' },
      icon: 'MessageSquare'
    },
    {
      id: 'supplier-applications',
      label: { en: 'Supplier Applications', zh: '供应商申请' },
      icon: 'UserCheck'
    },
    {
      id: 'coffee-management',
      label: { en: 'Coffee Management', zh: '咖啡管理' },
      icon: 'Coffee'
    },
    {
      id: 'suppliers',
      label: { en: 'Suppliers', zh: '供应商' },
      icon: 'Users'
    },
    {
      id: 'sponsor-banners',
      label: { en: 'Sponsor Banners', zh: '赞助广告' },
      icon: 'Image'
    },
    {
      id: 'translations',
      label: { en: 'Translations', zh: '翻译' },
      icon: 'Languages'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'reports':
        return <ReportsTab language={language} />;
      case 'order-lifecycle':
        return <OrderLifecycleTab language={language} />;
      case 'orders':
        return <OrdersTab language={language} />;
      case 'supplier-applications':
        return <SupplierApplicationsTab language={language} />;
      case 'coffee-management':
        return <CoffeeManagementTab language={language} />;
      case 'suppliers':
        return <SuppliersTab language={language} />;
      case 'sponsor-banners':
        return <SponsorBannersTab language={language} />;
      case 'translations':
        return <TranslationsTab language={language} />;
      default:
        return <ReportsTab language={language} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <div className="max-w-[1400px] mx-auto px-5 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  {language === 'en' ? 'Admin Dashboard' : '管理员仪表板'}
                </h1>
                <p className="text-muted-foreground">
                  {language === 'en' ? 'Manage your coffee platform content' : '管理您的咖啡平台内容'}
                </p>
              </div>
            </div>
          </div>

          <DashboardStats language={language} />

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-smooth whitespace-nowrap ${activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label?.[language]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;